import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import ThemeToggle from "../ThemeToggle";
import BrandLogo from "../BrandLogo";
import {
  LayoutDashboard,
  Link2,
  Store,
  Wallet,
  Users,
  Settings,
  FileText,
  LogOut,
  ChevronLeft,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

const affiliateNav = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/app" },
  { icon: Link2, label: "Referral Links", path: "/app/links" },
  { icon: Store, label: "Merchants", path: "/app/merchants" },
  { icon: Wallet, label: "Earnings", path: "/app/earnings" },
];

const adminNav = [
  { icon: LayoutDashboard, label: "Overview", path: "/admin" },
  { icon: Users, label: "Affiliates", path: "/admin/affiliates" },
  { icon: Store, label: "Merchants", path: "/admin/merchants" },
  { icon: Wallet, label: "Payouts", path: "/admin/payouts" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme } = useTheme();

  const isAdmin = user?.role === "admin";
  const isAdminSection = location.pathname.startsWith("/admin");
  
  const navItems = isAdminSection ? adminNav : affiliateNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-full border-r transition-all duration-300 z-40 flex flex-col",
        theme === 'dark' 
          ? 'bg-[#011a3a] border-white/5' 
          : 'bg-white border-slate-200',
        collapsed ? "w-20" : "w-64"
      )}
    >
      {/* Logo */}
      <div className={cn(
        "h-16 flex items-center justify-between px-4 border-b",
        theme === 'dark' ? 'border-white/5' : 'border-slate-200'
      )}>
        <Link to="/" className="flex items-center gap-2 overflow-hidden">
          {collapsed ? (
            <BrandLogo showMark className="w-10 h-10" />
          ) : (
            <BrandLogo className="h-8 w-auto" />
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className={theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}
          data-testid="sidebar-toggle"
        >
          <ChevronLeft className={cn("w-4 h-4 transition-transform", collapsed && "rotate-180")} />
        </Button>
      </div>

      {/* Role switch (for admins) */}
      {isAdmin && (
        <div className={cn(
          "px-4 py-3 border-b",
          theme === 'dark' ? 'border-white/5' : 'border-slate-200'
        )}>
          <div className={cn("flex gap-1", collapsed && "flex-col")}>
            <Button
              variant={isAdminSection ? "default" : "ghost"}
              size="sm"
              onClick={() => navigate("/admin")}
              className={cn("flex-1", collapsed && "px-2")}
              data-testid="switch-admin"
            >
              {collapsed ? "A" : "Admin"}
            </Button>
            <Button
              variant={!isAdminSection ? "default" : "ghost"}
              size="sm"
              onClick={() => navigate("/app")}
              className={cn("flex-1", collapsed && "px-2")}
              data-testid="switch-affiliate"
            >
              {collapsed ? "P" : "Portal"}
            </Button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                isActive
                  ? theme === 'dark' 
                    ? "bg-white/10 text-white" 
                    : "bg-blue-50 text-blue-600"
                  : theme === 'dark'
                    ? "text-slate-400 hover:text-white hover:bg-white/5"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              )}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Integration docs link */}
      <div className={cn(
        "px-3 py-2 border-t",
        theme === 'dark' ? 'border-white/5' : 'border-slate-200'
      )}>
        <Link
          to="/integration"
          data-testid="nav-integration"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all",
            theme === 'dark'
              ? "text-slate-400 hover:text-white hover:bg-white/5"
              : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          )}
        >
          <FileText className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="font-medium">Integration</span>}
        </Link>
      </div>

      {/* Theme toggle */}
      <div className={cn(
        "px-4 py-2 border-t flex justify-center",
        theme === 'dark' ? 'border-white/5' : 'border-slate-200'
      )}>
        <ThemeToggle />
      </div>

      {/* User section */}
      <div className={cn(
        "p-4 border-t",
        theme === 'dark' ? 'border-white/5' : 'border-slate-200'
      )}>
        <div className={cn("flex items-center gap-3", collapsed && "justify-center")}>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className={cn(
                "text-sm font-medium truncate",
                theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
              )}>
                {user?.name}
              </p>
              <p className={cn(
                "text-xs truncate",
                theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
              )}>
                {user?.email}
              </p>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className={cn(
            "w-full mt-3",
            theme === 'dark' 
              ? "text-slate-400 hover:text-white hover:bg-white/5"
              : "text-slate-600 hover:text-slate-900 hover:bg-slate-100",
            collapsed && "px-2"
          )}
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </aside>
  );
}
